import type { BulkAction } from '../singleactions/types';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { KizenTypography } from '__app/typography';
import { ButtonGroupImageButton } from 'components/Inputs/ButtonGroup/Button';
import {
  MODIFY_AUTOMATION_IDENTIFIER,
  START_AGENTIC_WORKFLOW_IDENTIFIER,
  SMART_CONNECTOR_IDENTIFIER,
  CUSTOM_ACTION_IDENTIFIER,
  JS_ACTION_IDENTIFIER,
  ARCHIVE_IDENTIFIER,
  CHANGE_FIELD_IDENTIFIER,
  EXPORT_IDENTIFIER,
  MANAGE_TEAM_IDENTIFIER,
  SEND_EMAIL_IDENTIFIER,
  SEND_TEXT_IDENTIFIER,
  SEND_SURVEY_IDENTIFIER,
  CHANGE_TAGS_IDENTIFIER,
  MANAGE_SUBSCRIPTIONS,
  AGENTIC_WORKFLOWS_ACTION_IDENTIFIERS,
  MANAGE_RECORDS_ACTION_IDENTIFIERS,
  COMMUNICATION_ACTION_IDENTIFIERS,
} from './model';

type GroupDef = {
  label: string;
  key: string;
  identifiers: string[];
};
import TypeModifyAutomations from 'pages/AutomationEngine/dialogs/ActionWizard/images/type-modify-automations.svg?react';
import TypeStartAutomations from 'pages/AutomationEngine/dialogs/ActionWizard/images/type-start-automations.svg?react';
import TypeBracketsCurly from 'pages/AutomationEngine/dialogs/ActionWizard/images/type-brackets-curly.svg?react';
import TypeArchiveRecord from 'pages/AutomationEngine/dialogs/ActionWizard/images/type-archive-record.svg?react';
import TypeChangeFieldValue from 'pages/AutomationEngine/dialogs/ActionWizard/images/type-change-field-value.svg?react';
import TypeAssignTeamMember from 'pages/AutomationEngine/dialogs/ActionWizard/images/type-assign-team-member.svg?react';
import TypeExportToCsv from './assets/type-export-to-csv.svg?react';
import TypeSmartConnector from './assets/type-smart-connector.svg?react';
import TypeLightningBolt from './assets/type-lightning-bolt.svg?react';
import TypeManageSubscriptions from './assets/type-manage-subscriptions.svg?react';
import TypeSendEmail from 'pages/AutomationEngine/dialogs/ActionWizard/images/type-send-email.svg?react';
import TypeNotifyEmail from 'pages/AutomationEngine/dialogs/ActionWizard/images/type-notify-email.svg?react';
import TypeNotifyText from 'pages/AutomationEngine/dialogs/ActionWizard/images/type-notify-text.svg?react';
import TypeChangeTags from 'pages/AutomationEngine/dialogs/ActionWizard/images/type-change-tags.svg?react';

type GroupedActionButtonsProps = {
  performActionOptions: BulkAction[];
  selectedOption: BulkAction | null;
  onSelect: (option: BulkAction) => void;
  groupDefs?: GroupDef[];
};

type ActionGroup = {
  label: string;
  key: string;
  options: BulkAction[];
};

const Section = styled.div`
  &:not(:last-child) {
    margin-bottom: 18px;
  }
  h3 {
    line-height: 1;
    margin-bottom: 11px;
  }
`;

const ButtonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
`;

const getActionIcon = (value: string) => {
  switch (value) {
    case MODIFY_AUTOMATION_IDENTIFIER:
      return <TypeModifyAutomations />;
    case START_AGENTIC_WORKFLOW_IDENTIFIER:
      return <TypeStartAutomations />;
    case SMART_CONNECTOR_IDENTIFIER:
      return <TypeSmartConnector />;
    case CUSTOM_ACTION_IDENTIFIER:
    case JS_ACTION_IDENTIFIER:
      return <TypeLightningBolt />;
    case ARCHIVE_IDENTIFIER:
      return <TypeArchiveRecord />;
    case CHANGE_FIELD_IDENTIFIER:
      return <TypeChangeFieldValue />;
    case EXPORT_IDENTIFIER:
      return <TypeExportToCsv />;
    case MANAGE_TEAM_IDENTIFIER:
      return <TypeAssignTeamMember />;
    case SEND_EMAIL_IDENTIFIER:
      return <TypeSendEmail />;
    case MANAGE_SUBSCRIPTIONS:
      return <TypeManageSubscriptions />;
    case SEND_SURVEY_IDENTIFIER:
      return <TypeNotifyEmail />;
    case SEND_TEXT_IDENTIFIER:
      return <TypeNotifyText />;
    case CHANGE_TAGS_IDENTIFIER:
      return <TypeChangeTags />;
    default:
      return <TypeBracketsCurly />;
  }
};

const getOptionKey = (option: BulkAction) => {
  if ('customAction' in option && option.customAction) {
    return `${option.value}-${option.customAction.id}`;
  }
  if ('jsAction' in option && option.jsAction) {
    return `${option.value}-${option.jsAction.name}`;
  }
  return option.value;
};

const isOptionSelected = (
  option: BulkAction,
  selectedOption: BulkAction | null
): boolean => {
  if (!selectedOption) return false;
  if (option.value !== selectedOption.value) return false;
  if (
    'customAction' in option &&
    option.customAction &&
    'customAction' in selectedOption &&
    selectedOption.customAction
  ) {
    return option.customAction.id === selectedOption.customAction.id;
  }
  if (
    'jsAction' in option &&
    option.jsAction &&
    'jsAction' in selectedOption &&
    selectedOption.jsAction
  ) {
    return option.jsAction.name === selectedOption.jsAction.name;
  }
  return true;
};

export const GroupedActionButtons = ({
  performActionOptions,
  selectedOption,
  onSelect,
  groupDefs: groupDefsProp,
}: GroupedActionButtonsProps) => {
  const { t } = useTranslation();

  const groups = useMemo<ActionGroup[]>(() => {
    const groupDefs = groupDefsProp ?? [
      {
        label: t('Agentic Workflows'),
        key: 'agentic_workflows',
        identifiers: AGENTIC_WORKFLOWS_ACTION_IDENTIFIERS,
      },
      {
        label: t('Manage Records'),
        key: 'manage_records',
        identifiers: MANAGE_RECORDS_ACTION_IDENTIFIERS,
      },
      {
        label: t('Communication'),
        key: 'communication',
        identifiers: COMMUNICATION_ACTION_IDENTIFIERS,
      },
    ];

    const allGroupedIdentifiers = groupDefs.flatMap((g) => g.identifiers);

    const groups = groupDefs
      .map(({ label, key, identifiers }) => ({
        label,
        key,
        options: identifiers.flatMap((id) =>
          performActionOptions.filter((opt) => opt.value === id)
        ),
      }))
      .filter((g) => g.options.length > 0);

    const ungrouped = performActionOptions.filter(
      (opt) => !allGroupedIdentifiers.includes(opt.value)
    );

    if (ungrouped.length > 0) {
      groups.push({ label: t('Other'), key: 'other', options: ungrouped });
    }

    return groups;
  }, [performActionOptions, groupDefsProp, t]);

  return (
    <>
      {groups.map(({ label, key, options }) => (
        <Section key={key}>
          <KizenTypography as="h3" type="subheader">
            {label}
          </KizenTypography>
          <ButtonGrid>
            {options.map((option) => (
              <ButtonGroupImageButton
                key={getOptionKey(option)}
                selected={isOptionSelected(option, selectedOption)}
                disabled={false}
                fieldId={undefined}
                onClick={() => onSelect(option)}
                option={{
                  label: option.label,
                  image: getActionIcon(option.value),
                }}
                data-qa-id={`perform-action-${option.value}`}
              />
            ))}
          </ButtonGrid>
        </Section>
      ))}
    </>
  );
};
